import { FROZEN_EMPTY_ARRAY } from '@common/constants';
import { TextDocuments } from '@common/types/messageTypes';
import {
  CreateGem,
  MetadataDidReload,
  TextDocumentDidCreate,
  TextDocumentDidDelete,
  TextDocumentDidMove,
  TextDocumentDidSwitch
} from '@LSP/base/hook';
import { PipelineLSPClient } from '@LSP/pipeline/client';
import { CodeViewMode, FileDefinitions, ViewType } from '@LSP/types';
import { lspToMonacoDiagnostic, lspToMonacoDiagnostics, lspToMonacoRange } from '@LSP/util';
import { PipelineInitializeParams } from '@pages/Workflow/types';
import { defaultDrawerheight } from '@prophecy/ui/Drawer';
import { ChangeDescriptor, FileOperation } from '@prophecy/ui/Editor/types';
import { useAfterNextRender, useDebounce, usePersistentCallback, useStateRef } from '@prophecy/utils/react/hooks';
import { isCaseInsensitiveEqual } from '@prophecy/utils/string';
import { WritableDraft } from 'immer/dist/internal';
import { noop } from 'lodash-es';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useContextSelector } from 'use-context-selector';
import { Updater, useImmer } from 'use-immer';

import { WorkflowInterimMode } from '../../constants/workflow';
import { BackEndCodeLanguage, BackEndCodeLanguageType } from '../../graphqlTypes/enums';
import {
  CHAT_PANEL_WIDTH_KEY,
  CODE_VIEW_OPENED_FILE_INFO,
  PROJECT_BROWSER_OPEN_KEY,
  PROJECT_BROWSER_WIDTH_KEY
} from '../../utils/localstorage-keys';
import { ProjectWiseFunctions } from '../ProjectBrowser/Functions/types';
import { getFilePathWithEntityId } from '../ProjectBrowser/utils';
import { CommonGraph, CommonState, SparkSourceSuggestion } from '../types';
import { Entity, EntityToCreate, IDEEntity } from '../types/Entity';
import { AddProcessType } from '../utils/graph/process';
import {
  type IdeState,
  IdeStateContext,
  type ActionContextType,
  IdeActionsContext,
  ChangeViewType,
  type UpdateFilesParams,
  type SetFilesParams,
  type TextDocumentsWithDiagnostic,
  type SelectedFooterCardState,
  type IdeActions,
  type FilesMap,
  type SelectedFileType,
  type FocusNodeInfo,
  defaultGetEntityRootGraph,
  type GemState
} from './types';
import { useIDEParamsForIDEPage } from './useIDEParamsForIDEPage';
import { toIDEEntity } from './utils';

export const DEFAULT_PROJECT_BROWSER_WIDTH = 320;
export const DEFAULT_CHAT_PANEL_WIDTH = 470;
export const CHAT_PANEL_OPEN_KEY = 'chat-panel-open';
export const PROJECT_SEARCH_CLASS = 'ide-project-search';

const PRIMARY_FILE_REGEX = /\/(main.(scala|sql)|dag\.py|pipeline\.py)$/;
const DATABRICKS_JOB_PRIMARY_FILE_REGEX = /\/(prophecy-job\.json)$/;
const SUBGRAPH_FILE_REGEX = /\/(package.(scala|sql)|dag\.py|pipeline\.py)$/;

export function getGraphPath(primaryFilePath?: string) {
  return primaryFilePath
    ? `${primaryFilePath.replace(PRIMARY_FILE_REGEX, '').replace(DATABRICKS_JOB_PRIMARY_FILE_REGEX, '')}/graph`
    : undefined;
}

const getDefaultWidthForCanvasArea = () => {
  const totalContainerWidth = document.body.clientWidth;
  const SIDE_NAV_BAR_WIDTH = 70;
  return totalContainerWidth - SIDE_NAV_BAR_WIDTH;
};

function getPrimaryFileURI(files: FilesMap, entity: Entity) {
  const fileURIs = Object.keys(files);

  let primaryFileURI = fileURIs.find((path) => {
    if (entity === Entity.Subgraph) {
      return path.match(SUBGRAPH_FILE_REGEX);
    }

    return path.match(PRIMARY_FILE_REGEX);
  });

  // if primary file not found, use the substitute file
  if (!primaryFileURI) {
    primaryFileURI = fileURIs.find((path) => path.match(PRIMARY_FILE_REGEX));
  }

  return primaryFileURI;
}

export function IdeStateProvider({ value, children }: { value: IdeState; children: React.ReactNode }) {
  return <IdeStateContext.Provider value={value}>{children}</IdeStateContext.Provider>;
}
export function IdeActionProvider({ value, children }: { value: ActionContextType; children: React.ReactNode }) {
  const memoizedValue = useMemo(
    () => ({ actions: value.actions, setActions: value.setActions }),
    [value.actions, value.setActions]
  );
  return <IdeActionsContext.Provider value={memoizedValue}>{children}</IdeActionsContext.Provider>;
}
const prefix = '___';

const getSecondaryFileKey = (entity: Entity) => CODE_VIEW_OPENED_FILE_INFO + '_' + entity;
export function getLastSecondaryFileOpenedKey(entity: Entity) {
  const codeViewFileOpened = localStorage.getItem(getSecondaryFileKey(entity))?.split(prefix);
  return { uid: codeViewFileOpened?.[0], path: codeViewFileOpened?.[1], entity: codeViewFileOpened?.[2] };
}

function setLastSecondaryFileOpenedKey(uid: string, filePath: string, entity: string, baseEntity: Entity) {
  localStorage.setItem(getSecondaryFileKey(baseEntity), uid + prefix + filePath + prefix + entity);
}

export const getDefaultChatPanelWidth = (coveredAreaPercentage = 0.4) => {
  const defaultContainerWidth = getDefaultWidthForCanvasArea();
  return defaultContainerWidth > 0 ? defaultContainerWidth * coveredAreaPercentage : DEFAULT_CHAT_PANEL_WIDTH;
};

export function useIDE({
  uid,
  entity: ideEntity,
  projectId,
  projectName,
  language,
  repositoryPath,
  branch,
  version,
  path,
  defaultShowProjectBrowser,
  getEntityRootGraph = defaultGetEntityRootGraph
}: {
  uid: string;
  entity: IDEEntity;
  projectId: string;
  projectName: string;
  language: BackEndCodeLanguageType;
  repositoryPath?: string;
  branch?: string;
  version?: string;
  path?: string;
  defaultShowProjectBrowser?: boolean;
  getEntityRootGraph?: IdeActions['getEntityRootGraph'];
}) {
  const selectedFileMap = { entityId: uid, selectedEntity: ideEntity, filePath: '' };
  const hasUserChoiceSetForPB = localStorage.getItem(PROJECT_BROWSER_OPEN_KEY);
  const hasUserChoiceSetForCW = localStorage.getItem(CHAT_PANEL_OPEN_KEY) ?? 'true';
  const projectBrowserUserChoiceWidth = localStorage.getItem(PROJECT_BROWSER_WIDTH_KEY) ?? '0';
  const chatPanelUserChoiceWidth = localStorage.getItem(CHAT_PANEL_WIDTH_KEY) ?? '0';
  const showProjectBrowser = defaultShowProjectBrowser ?? hasUserChoiceSetForPB === 'true' ?? false;
  const showChatWindow = hasUserChoiceSetForCW === 'true' ?? true;
  const getDefaultChatPanelWidth = () => {
    if (Number(chatPanelUserChoiceWidth) > 0) {
      return Number(chatPanelUserChoiceWidth);
    }
    const defaultContainerWidth = getDefaultWidthForCanvasArea();
    const coveredArea = 0.4;

    return defaultContainerWidth > 0 ? defaultContainerWidth * coveredArea : DEFAULT_CHAT_PANEL_WIDTH;
  };
  //Set chatPanel width by default big on spark side
  const isSpark = language !== BackEndCodeLanguage.sql;
  const [state, setState] = useImmer<IdeState>({
    project: {
      id: projectId,
      name: projectName,
      language
    },
    hasProjectBrowser: showProjectBrowser,
    hasChatPanel: showChatWindow,
    projectBrowserWidth:
      Number(projectBrowserUserChoiceWidth) > 0
        ? Number(projectBrowserUserChoiceWidth)
        : isSpark
          ? DEFAULT_CHAT_PANEL_WIDTH
          : DEFAULT_PROJECT_BROWSER_WIDTH,
    chatPanelWidth: getDefaultChatPanelWidth(),
    footerDrawerHeight: defaultDrawerheight,
    footerDrawerOpen: false,
    ide: {
      uid,
      runAppOnceMap: {},
      activeDialogType: undefined,
      entity: ideEntity,
      branch,
      path,
      version,
      repositoryPath,
      viewType: ViewType.visual,
      defaultExpandAll: false,
      files: {},
      selectedFiles: [],
      monitoringSelectedFiles: {},
      codeViewMode: ideEntity === Entity.Configuration ? CodeViewMode.single : CodeViewMode.multiple,
      entityTypeToCreate: undefined,
      gemIde: {
        showGemPreview: false
      },
      pipelineAppsMap: {},
      disabledServerlessFormats: []
    },
    graph: {}
  });

  useEffect(() => {
    setState((draft) => {
      draft.ide.uid = uid;
      draft.ide.repositoryPath = repositoryPath;
      draft.ide.branch = branch;
      draft.ide.version = version;
      draft.ide.path = path;
    });
  }, [uid, repositoryPath, branch, version, path, setState]);

  const setEntity = usePersistentCallback(function setEntity({
    uid,
    entity,
    branch,
    version,
    repositoryPath
  }: {
    uid: string;
    entity: IDEEntity;
    repositoryPath?: string;
    branch?: string;
    version?: string;
  }) {
    setState((draft) => {
      const lastEntity = draft.ide.entity;
      draft.ide.uid = uid;
      draft.ide.repositoryPath = repositoryPath;
      draft.ide.entity = entity;
      draft.ide.branch = branch;
      draft.ide.version = version;
      draft.ide.viewType = ViewType.visual;
      draft.ide.defaultExpandAll = false;
      draft.ide.runAppOnceMap = {};
      draft.ide.files = {};
      //Need to set it initially because setFiles and change view are getting asynchronously, resulting in primary path is being empty when change view
      draft.ide.selectedFiles = [selectedFileMap];
      draft.ide.codeViewMode =
        entity === Entity.Gem || entity === Entity.Configuration || entity === Entity.Observation
          ? CodeViewMode.single
          : CodeViewMode.multiple;

      //reset function on entity change
      if (lastEntity !== entity) {
        draft.ide.functions = undefined;
      }
    });
  });

  useEffect(() => {
    setEntity({ uid, entity: ideEntity, branch, repositoryPath });
  }, [uid, ideEntity, branch, setEntity, repositoryPath]);
  // addProcess/metadataDidReload(requires redux context) are later updated via useSetIdeAction, this is done to avoid moving redux to top
  const defaultSelectedFileMap = { entityId: repositoryPath ?? uid, selectedEntity: ideEntity, filePath: '' };

  const [actions, setActions] = useCreateIDEActions(
    defaultSelectedFileMap,
    ideEntity,
    state,
    setState,
    getEntityRootGraph
  );
  return { state, actions, setActions };
}
function useCreateIDEActions(
  defaultSelectedFileMap: SelectedFileType,
  ideEntity: Entity,
  state: IdeState,
  setState: Updater<IdeState>,
  getEntityRootGraph: IdeActions['getEntityRootGraph']
) {
  const setProjectBrowserWidth = usePersistentCallback(function setProjectBrowserWidth(
    width: number,
    hasProjectBrowser: boolean
  ) {
    // set the information on local storage so next time the project browser will remain open
    localStorage.setItem(PROJECT_BROWSER_OPEN_KEY, hasProjectBrowser.toString());
    localStorage.setItem(PROJECT_BROWSER_WIDTH_KEY, `${width}`);
    setState((draft) => {
      draft.projectBrowserWidth = width;
      draft.hasProjectBrowser = hasProjectBrowser;
    });
  });

  const toggleRunAppOnce = usePersistentCallback(function toggleRunAppOnce(entityName, isRunAppOnce: boolean) {
    setState((draft) => {
      if (draft.ide.runAppOnceMap[entityName] === undefined) {
        draft.ide.runAppOnceMap[entityName] = isRunAppOnce;
      }
    });
  });

  const setChatPanelWidth = usePersistentCallback(function setChatPanelWidth(width: number, hasChatPanel: boolean) {
    // set the information on local storage so next time the chat panel will remain open
    localStorage.setItem(CHAT_PANEL_OPEN_KEY, Boolean(hasChatPanel).toString());
    localStorage.setItem(CHAT_PANEL_WIDTH_KEY, `${width}`);
    setState((draft) => {
      draft.chatPanelWidth = width;
      draft.hasChatPanel = hasChatPanel;
    });
  });

  const setPipelineAppMap = usePersistentCallback(function setPipelineAppMap(
    projectId: string,
    closedApps: string[],
    totalOpenedApps: string[],
    overflowItems: string[]
  ) {
    setState((draft) => {
      draft.ide.pipelineAppsMap[projectId] = { closedApps, totalOpenedApps, overflowItems };
    });
  });

  const setFooterDrawerHeight = usePersistentCallback(function setLogsDrawerHeight(height: number) {
    setState((draft) => {
      draft.footerDrawerHeight = height;
    });
  });
  const setFooterDrawerOpen = usePersistentCallback(function setFooterDrawerOpen(open: boolean) {
    setState((draft) => {
      draft.footerDrawerOpen = open;
    });
  });
  const setSourceSuggestions = usePersistentCallback(function (sparkSourceSuggestions?: SparkSourceSuggestion[]) {
    setState((draft) => {
      draft.ide.sparkSourceSuggestions = sparkSourceSuggestions;
    });
  });
  const setReadOnly = usePersistentCallback(function setReadOnly(isReadOnly?: boolean) {
    setState((draft) => {
      draft.ide.isReadOnly = isReadOnly;
    });
  });
  const setInterimMode = usePersistentCallback(function setReadOnly(interimMode: WorkflowInterimMode) {
    setState((draft) => {
      draft.graph.interimMode = interimMode;
    });
  });
  const setInterimModeEnabled = usePersistentCallback(function setInterimModeEnabled(interimModeEnabled: boolean) {
    setState((draft) => {
      draft.graph.interimModeEnabled = interimModeEnabled;
    });
  });

  const toggleActiveDialog = usePersistentCallback((value = '') => {
    setState((draft) => {
      draft.ide.activeDialogType = value;
    });
  });
  const setEntity = usePersistentCallback(function setEntity({
    uid,
    entity,
    branch,
    version,
    repositoryPath
  }: {
    uid: string;
    entity: IDEEntity;
    repositoryPath?: string;
    branch?: string;
    version?: string;
  }) {
    setState((draft) => {
      draft.ide.uid = uid;
      draft.ide.repositoryPath = repositoryPath;
      draft.ide.entity = entity;
      draft.ide.branch = branch;
      draft.ide.version = version;
      draft.ide.viewType = ViewType.visual;
      draft.ide.defaultExpandAll = false;
      draft.ide.files = {};
      draft.ide.sparkSourceSuggestions = undefined;
      //Need to set it initially because setFiles and change view are getting asynchronously, resulting in primary path is being empty when change view
      draft.ide.selectedFiles = [defaultSelectedFileMap];
      draft.ide.codeViewMode =
        entity === Entity.Gem || entity === Entity.Configuration || entity === Entity.Observation
          ? CodeViewMode.single
          : CodeViewMode.multiple;
    });
  });

  const setMonitoringSelectedFile = usePersistentCallback(
    (filePath: string, entityId: string, selectedEntity: Entity) => {
      setState((draft) => {
        const previousEntity = draft.ide.monitoringSelectedFiles[selectedEntity];
        if (previousEntity?.selectedEntity && previousEntity?.entityId === entityId) {
          draft.ide.monitoringSelectedFiles[Entity.Observation] = { filePath: '', entityId: '', selectedEntity };
          delete draft.ide.monitoringSelectedFiles[selectedEntity];
        } else {
          draft.ide.monitoringSelectedFiles[selectedEntity] = { filePath, entityId, selectedEntity };
          draft.ide.monitoringSelectedFiles[Entity.Observation] = { filePath, entityId, selectedEntity };
        }
      });
    }
  );

  const debouncedSetState = useDebounce(setState, 300);
  const setSelectedGem = usePersistentCallback((gemKind?: string, gemCategory?: string, debounced: boolean = false) => {
    if (gemKind || gemCategory) {
      const stateSetter = debounced ? debouncedSetState : setState;
      stateSetter((draft) => {
        draft.ide.selectedGem = { gemKind, gemCategory };
      });
    } else {
      debouncedSetState.cancel();
      setState((draft) => {
        draft.ide.selectedGem = undefined;
      });
    }
  });

  const toggleFileLoader = usePersistentCallback((entityId: string) => {
    setState((draft) => {
      draft.ide.files[entityId] = draft.ide.files[entityId] || { files: {}, loading: false };
      draft.ide.files[entityId].loading = true;
    });
  });

  const getSelectedFiles = () => {
    return state.ide.selectedFiles.length > 0 ? state.ide.selectedFiles : [defaultSelectedFileMap];
  };

  const changeViewType = usePersistentCallback(function changeViewType(...args: Parameters<ChangeViewType>) {
    const [viewType, cleanFiles = true] = args;
    setState((draft) => {
      draft.ide.viewType = viewType;
      // Reset code view files
      if (cleanFiles && viewType === ViewType.visual) {
        draft.ide.files = {};
      }

      if (viewType === ViewType.code) {
        draft.hasProjectBrowser = true;
        draft.projectBrowserWidth = draft.projectBrowserWidth ?? DEFAULT_PROJECT_BROWSER_WIDTH;
      } else {
        draft.projectBrowserWidth =
          (localStorage.getItem(PROJECT_BROWSER_OPEN_KEY) === 'true' ?? false) ? DEFAULT_PROJECT_BROWSER_WIDTH : 0;
        draft.hasProjectBrowser = draft.projectBrowserWidth > 0;
      }
    });
  });
  const setSelectedFile = usePersistentCallback(function setSelectedFile(filePath: string, entityId: string) {
    setState((draft) => {
      const { entity: selectedEntity } = draft.ide.files[entityId];

      if (draft.ide.codeViewMode === CodeViewMode.multiple) {
        draft.ide.selectedFiles[1] = { entityId, selectedEntity, filePath };
        setLastSecondaryFileOpenedKey(entityId, filePath, selectedEntity, ideEntity);
      } else {
        draft.ide.selectedFiles[0].filePath = filePath;
      }
    });
  });
  const updateFiles = usePersistentCallback(function setFiles({ textDocuments, uid: entityId }: UpdateFilesParams) {
    setState((draft) => {
      textDocuments.forEach((item) => {
        const file = draft.ide.files[entityId].files[item.uri];
        if (file) {
          file.code = item.text ?? file.code;
          file.language = item.language ?? file.language;
          file.fileType = item.fileType ?? file.fileType;
          file.readOnly = item.editable ?? file.readOnly;
          // Note: here we only update code/language/fileType/readOnly. for definitions,diagnostics use changeFileMetadata/filesDiagnostics
        }
      });
      //
    });
  });
  const setFiles = usePersistentCallback(function setFiles({
    textDocuments,
    uid: entityId,
    entity: selectedEntity,
    name,
    loadFiles = true,
    primaryFilePath: _primaryFilePath
  }: SetFilesParams) {
    setState((draft) => {
      const files = getFilesData(textDocuments);

      const primaryFileURI = getPrimaryFileURI(files, selectedEntity) || textDocuments[0]?.uri;

      const primaryFilePath = _primaryFilePath
        ? getFilePathWithEntityId(_primaryFilePath, entityId)
        : getFilePathWithEntityId(primaryFileURI, entityId);
      let secondaryFilePath: string;
      draft.ide.selectedFiles = getSelectedFiles();
      draft.ide.files[entityId] = {
        files: files as WritableDraft<FilesMap>,
        loading: false,
        entity: selectedEntity,
        name
      };

      const lastOpenedFileKeys = getLastSecondaryFileOpenedKey(ideEntity);
      secondaryFilePath = lastOpenedFileKeys.path ?? '';

      const lastOpenedUId = lastOpenedFileKeys.uid || entityId;
      const lastOpenedFile = secondaryFilePath;
      const lastOpenedEntity = lastOpenedFileKeys.entity || selectedEntity;
      if (loadFiles) {
        if (selectedEntity === ideEntity || draft.ide.codeViewMode !== CodeViewMode.multiple) {
          draft.ide.selectedFiles[0] = { selectedEntity, entityId, filePath: primaryFilePath };
        }
        if (draft.ide.codeViewMode === CodeViewMode.multiple && lastOpenedUId === entityId) {
          draft.ide.selectedFiles[1] = {
            selectedEntity: lastOpenedEntity as Entity,
            entityId: lastOpenedUId,
            filePath: lastOpenedFile
          };
          setLastSecondaryFileOpenedKey(lastOpenedUId, lastOpenedFile, lastOpenedEntity, ideEntity);
        } else {
          draft.ide.selectedFiles[1] = { selectedEntity, entityId: '', filePath: '' };
        }
      }
    });
  });
  const changeFile = usePersistentCallback(function changeFile(changes: ChangeDescriptor[], entityId: string) {
    setState((draft) => {
      changes.forEach((change) => {
        const { type, path, text } = change;
        if (type === FileOperation.UPDATE && draft.ide.files?.[entityId]?.files?.[path]) {
          draft.ide.files[entityId].files[path].code = text;
        }
      });
    });
  });
  const changeFileMetadata = usePersistentCallback(function changeFileMetadata(
    fileDefinitions: FileDefinitions,
    entityId: string
  ) {
    setState((draft) => {
      const { uri, definitions } = fileDefinitions;

      draft.ide.files[entityId].files[uri].definitions = definitions.map(({ range, ...rest }) => ({
        range: {
          from: { line: range.start.line, column: range.start.character },
          to: { line: range.end.line, column: range.end.character }
        },
        ...rest
      }));
    });
  });

  const filesDiagnostics = usePersistentCallback(function filesDiagnostics(
    files: TextDocumentsWithDiagnostic,
    entityId: string
  ) {
    setState((draft) => {
      files.forEach((item) => {
        const file = draft.ide.files[entityId].files[item.uri];
        if (file) {
          file.diagnostics = (item.diagnostics || []).map(lspToMonacoDiagnostic);
        }
      });
    });
  });

  const setEntityTypeToCreate = usePersistentCallback(function setEntityTypeToCreate(
    entityTypeToCreate: EntityToCreate
  ) {
    setState((draft) => {
      draft.ide.entityTypeToCreate = entityTypeToCreate;
    });
  });

  const markToReloadMetadata = usePersistentCallback(function markToReloadMetadata(needsMetadataReload: boolean) {
    setState((draft) => {
      draft.ide.needsMetadataReload = needsMetadataReload;
    });
  });

  const setSelectedFooterCard = usePersistentCallback(function setSelectedFooterCard(
    selectedFooterCard?: SelectedFooterCardState
  ) {
    setState((draft) => {
      draft.ide.selectedFooterCard = selectedFooterCard;
    });
  });

  const setActiveLogIndex = usePersistentCallback(function setActiveLogIndex(index?: number | undefined) {
    setState((draft) => {
      if (draft.ide.selectedFooterCard) draft.ide.selectedFooterCard.activeLogIndex = index;
    });
  });

  const setSearchQuery = usePersistentCallback(function setSearchQuery(query: string) {
    setState((draft) => {
      draft.ide.searchQuery = query;
    });
  });

  const focusNodeInGraph = usePersistentCallback(function focusNodeInGraph(nodeToFocus?: FocusNodeInfo) {
    setState((draft) => {
      draft.graph.nodeToFocus = nodeToFocus;
    });
  });

  const setGraphPath = usePersistentCallback(function setGraphPath(graphPath?: string) {
    setState((draft) => {
      draft.graph.graphPath = graphPath;
    });
  });

  const setSelectedNodeIds = usePersistentCallback(function setSelectedNodeIds(nodeIds: string[]) {
    setState((draft) => {
      draft.graph.selectedNodeIds = nodeIds;
    });
  });

  const setGemList = usePersistentCallback((gemList) => {
    setState((draft) => {
      draft.ide.gemList = gemList;
    });
  });

  const setFunctions = usePersistentCallback((functions: ProjectWiseFunctions) => {
    setState((draft) => {
      if (JSON.stringify(draft.ide.functions) !== JSON.stringify(functions)) {
        draft.ide.functions = functions;
      }
    });
  });
  const setPipelineLspClient = usePersistentCallback((lspClient?: PipelineLSPClient<PipelineInitializeParams>) => {
    setState((draft) => {
      draft.pipelineLspClient = lspClient;
    });
  });
  const setGemState = usePersistentCallback((state: Partial<GemState>) => {
    setState((draft) => {
      draft.ide.gemIde = { ...draft.ide.gemIde, ...state };
    });
  });
  const { afterNextRender } = useAfterNextRender();
  const openSearch = usePersistentCallback(() => {
    setProjectBrowserWidth(
      state.projectBrowserWidth !== 0 ? state.projectBrowserWidth : DEFAULT_PROJECT_BROWSER_WIDTH,
      true
    );

    afterNextRender(() => {
      afterNextRender(() => {
        const searchInput = document.querySelector(`.${PROJECT_SEARCH_CLASS} input`) as HTMLInputElement | null;
        searchInput?.focus();
      });
    });
  });

  const setDisabledServerlessFormats = usePersistentCallback((disabledServerlessFormats: string[]) => {
    setState((draft) => {
      draft.ide.disabledServerlessFormats = disabledServerlessFormats;
    });
  });

  const setIsRemapping = usePersistentCallback((isRemapping: boolean) => {
    setState((draft) => {
      draft.ide.isRemapping = isRemapping;
    });
  });

  // addProcess/metadataDidReload(requires redux context) are later updated via useSetIdeAction, this is done to avoid moving redux to top
  const addProcess = noop as AddProcessType<CommonGraph>;

  const actions = {
    sourceSuggestionClickHandler: noop,
    setHoveredSuggestion: noop,
    setSourceSuggestions,
    setFiles,
    updateFiles,
    reinitializeLSPconnection: noop,
    toggleFileLoader,
    setSelectedFile,
    setInterimMode,
    setInterimModeEnabled,
    toggleActiveDialog,
    setMonitoringSelectedFile,
    setSelectedGem,
    changeViewType,
    toggleRunAppOnce,
    addProcess,
    refreshDependencies: noop,
    setEntity,
    setActiveLogIndex,
    metadataDidReload: noop as MetadataDidReload,
    textDocumentDidCreate: noop as TextDocumentDidCreate,
    textDocumentDidDelete: noop as TextDocumentDidDelete,
    textDocumentDidMove: noop as TextDocumentDidMove,
    textDocumentDidSwitch: noop as TextDocumentDidSwitch,
    createGem: noop as CreateGem,
    changeFile,
    changeFileMetadata,
    filesDiagnostics,
    setProjectBrowserWidth,
    setChatPanelWidth,
    setFooterDrawerHeight,
    setFooterDrawerOpen,
    setReadOnly,
    setEntityTypeToCreate,
    markToReloadMetadata,
    setSelectedFooterCard,
    focusNodeInGraph,
    changeGraphPath: noop,
    setSearchQuery,
    setGraphPath,
    setSelectedNodeIds,
    setGemList,
    setFunctions,
    setPipelineAppMap,
    addFunction: noop,
    removeFunction: noop,
    searchNodes: () => [],
    openSearch,
    getEntityRootGraph,
    setGemState,
    setPipelineLspClient,
    setDisabledServerlessFormats,
    scrollToMessage: () => Promise.resolve(),
    addPipelineToJobIDE: noop,
    setIsRemapping
  };

  return useStateRef<IdeActions>(actions);
}
export function useOldPlayground({ uid, entity: ideEntity }: { uid: string; entity: IDEEntity }) {
  const [state, setState] = useImmer<IdeState>({
    project: {
      id: '',
      name: '',
      language: 'scala' //dummy
    },
    hasChatPanel: false,
    hasProjectBrowser: false,
    projectBrowserWidth: 0,
    chatPanelWidth: DEFAULT_CHAT_PANEL_WIDTH,
    footerDrawerHeight: defaultDrawerheight,
    footerDrawerOpen: false,
    ide: {
      uid,
      runAppOnceMap: {},
      entity: ideEntity,
      viewType: ViewType.visual,
      defaultExpandAll: false,
      files: {},
      pipelineAppsMap: {},
      selectedFiles: [],
      monitoringSelectedFiles: {},
      codeViewMode: CodeViewMode.single,
      entityTypeToCreate: undefined,
      gemIde: {
        showGemPreview: false
      },
      disabledServerlessFormats: FROZEN_EMPTY_ARRAY as string[]
    },
    graph: {}
  });
  const defaultSelectedFileMap = { entityId: uid, selectedEntity: ideEntity, filePath: '' };
  const [actions, setActions] = useCreateIDEActions(
    defaultSelectedFileMap,
    ideEntity,
    state,
    setState,
    defaultGetEntityRootGraph
  );
  return { state, actions, setActions };
}

export function useIdeState<T>(selector: (state: IdeState) => T) {
  return useContextSelector<IdeState | null, T>(IdeStateContext, (state) => {
    return selector(state as IdeState);
  });
}

export function useIdeActions() {
  return useContext(IdeActionsContext).actions.current;
}

export function useSetIdeAction() {
  const { setActions } = useContext(IdeActionsContext);
  const setIDEActions = usePersistentCallback((actions: Partial<IdeActions>) => {
    setActions((prevActions) => ({ ...prevActions, ...actions }));
  });
  return { setIDEActions };
}

export type IDEParamsType = {
  uid: string;
  entity: string;
  branch?: string;
  'run-id'?: string;
  projectId?: string;
  action?: string;
  gemId?: string;
  observedEntity?: string;
  transpilerGemId?: string;
  transpilerVersion?: string;
  transpilerSource?: string;
  transpilerId?: string;
};

const IDEParamsContext = createContext<IDEParamsType | undefined>(undefined);
export function IDEParamsProvider({ params, children }: { params: IDEParamsType; children: React.ReactNode }) {
  return <IDEParamsContext.Provider value={params}>{children}</IDEParamsContext.Provider>;
}
export const useIDEParams = () => {
  const _context = useContext(IDEParamsContext) as IDEParamsType;
  const defaultContext = useIDEParamsForIDEPage();
  const context = _context ?? defaultContext;
  const uid = context.uid;
  const _entity = context.entity;

  const branch = context.branch as string;
  const pipelineRunId = context['run-id'] as string;
  const projectId = context.projectId as string;
  const action = context.action as string;
  const gemId = context.gemId as string;
  const observedEntity = context.observedEntity as string;
  const entity = toIDEEntity(_entity);
  const isUIDFromAnotherProject = Boolean(projectId);

  const transpilerVersion = context.transpilerVersion as string;
  const transpilerSource = context.transpilerSource as string;
  const transpilerId = context.transpilerId as string;
  const transpilerGemId = context.transpilerGemId as string;

  if (entity === Entity.Lineage) {
    // for Lineage, /ide/:entity/:uid -> uid is project id
    return {
      uid: uid,
      entity,
      branch,
      action,
      projectId: uid,
      isUIDFromAnotherProject: false
    };
  }
  if (entity === Entity.Gem) {
    // for gems, /ide/:entity/:uid -> uid is project id & gem id is passed in query params
    return {
      uid: gemId,
      action,
      entity,
      branch,
      projectId: uid,
      isUIDFromAnotherProject: isUIDFromAnotherProject
    };
  }
  return {
    isUIDFromAnotherProject: isUIDFromAnotherProject,
    pipelineRunId,
    uid,
    entity,
    action,
    transpilerVersion,
    transpilerGemId,
    transpilerSource,
    transpilerId,
    branch,
    projectId: projectId,
    observedEntity
  };
};
function getFilesData(textDocuments: TextDocuments) {
  const files: FilesMap = {};
  textDocuments.forEach((document) => {
    const path = `${document.uri}`;

    const { language, editable, text, fileType, deletable } = document;

    // get definitions
    const definitions = document.definitions?.map(({ range, referencePath }) => {
      return {
        range: lspToMonacoRange(range),
        referencePath: `${referencePath}`
      };
    });

    // get diagonistics
    const diagnostics = lspToMonacoDiagnostics(document.diagnostics || []);

    files[path] = {
      path,
      language,
      fileType,
      code: text,
      readOnly: !editable,
      definitions,
      diagnostics,
      canDelete: deletable
    };
  });

  return files;
}

export const useMetadataReloadAction = () => {
  const entity = useIdeState((state) => state?.ide.entity);
  const isSparkIDE =
    isCaseInsensitiveEqual(entity, Entity.Pipeline) ||
    isCaseInsensitiveEqual(entity, Entity.Configuration) ||
    isCaseInsensitiveEqual(entity, Entity.Job);
  const { metadataDidReload, reinitializeLSPconnection } = useIdeActions();
  const reloadMetadataOrReinitializeConnection = async () => {
    if (isSparkIDE) {
      reinitializeLSPconnection();
    } else {
      await metadataDidReload();
    }
  };

  return { reloadMetadataOrReinitializeConnection };
};

type EntityRootGraph<T extends CommonGraph> = {
  graph?: T;
  graphPath: string;
};

export function useEntityRootGraph<T extends CommonGraph = CommonGraph, R = EntityRootGraph<T>>(
  selector?: (arg: { graph?: T; graphPath?: string }) => R
): R {
  const { getEntityRootGraph } = useIdeActions();

  return useSelector((state: CommonState) => {
    const { graph, graphPath } = getEntityRootGraph(state);
    return selector ? selector({ graph: graph as T, graphPath }) : ({ graph, graphPath } as R);
  }, shallowEqual);
}

export const useInterimConfig = () => {
  const interimModeEnabled = useIdeState((state) => state?.graph?.interimModeEnabled);
  const interimMode = useIdeState((state) => state?.graph?.interimMode);
  return { interimModeEnabled, interimMode };
};
